import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DaypartDay, Particularity, Ride, User, Vehicle } from 'src/app/interfaces';
import moment from 'moment';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { collection, doc, getDoc, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { auth, dataStore, db } from 'src/app/app.component';
import { updateRideToV2 } from 'src/app/planner/helpers/ride-helper';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { EventItemComponent } from '../../../../../planner/components/event-item/event-item.component';
import { Unsubscribe } from 'firebase/auth';

@Component({
  selector: 'app-daypart-ride',
  templateUrl: './daypart-ride.component.html',
  styleUrls: ['./daypart-ride.component.scss'],
  imports: [MatIcon, MatIconButton, RouterLink, EventItemComponent],
})
export class DaypartRideComponent implements OnInit, OnDestroy {
  moment = moment;
  daypartId: string = this.route.snapshot.paramMap.get('id') ?? '';
  type: string = this.route.snapshot.paramMap.get('type') ?? '';
  driver: User | null = null;
  vehicle: Vehicle | null = null;
  daypartDay: DaypartDay | null = null;
  organisationId: string = dataStore.selectedOrganisation.id;
  rides = signal<Ride[]>([]);
  loading = signal(true);
  firstUpcomingRide: Ride | null = null;
  particularities: Particularity[] = dataStore.selectedOrganisation.particularities ?? [];
  userId: string = auth.currentUser?.uid!;
  driverId: string = this.route.snapshot.queryParamMap.get('userId') ?? '';
  drivers = signal<User[]>([]);
  ridesUnsub?: Unsubscribe;

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<any> {
    this.daypartDay = (
      await getDoc(doc(db, `organisations/${this.organisationId}/daypartDays/${this.daypartId}`))
    ).data() as DaypartDay;
    this.daypartDay.start = this.daypartDay.start.toDate();
    this.daypartDay.end = this.daypartDay.end.toDate();
    // console.log('daypartDay', this.daypartDay);
    this.driverId =
      this.type === 'driver' ? this.daypartDay.driverId! : this.daypartDay.coDriverId!;

    this.vehicle = (
      await getDoc(
        doc(db, `organisations/${this.organisationId}/vehicles/${this.daypartDay.vehicleId}`),
      )
    ).data() as Vehicle;

    this.driver = (
      await getDoc(
        doc(
          db,
          `organisations/${this.organisationId}/users/${this.type === 'driver' ? this.daypartDay.driverId : this.daypartDay.coDriverId}`,
        ),
      )
    ).data() as User;
    this.driver.id = this.type === 'driver' ? this.daypartDay.driverId : this.daypartDay.coDriverId;
    this.drivers.set([this.driver]);

    const daypartStart = moment(this.daypartDay.start);
    const ridesQuery = query(
      collection(db, `organisations/${this.organisationId}/rides`),
      where('vehicleId', '==', this.daypartDay.vehicleId),
      where('start', '>=', daypartStart.startOf('day').toDate()),
      where('end', '<=', daypartStart.endOf('day').toDate()),
      orderBy('start'),
    );

    this.ridesUnsub = onSnapshot(
      ridesQuery,
      async (snapshot) => {
        this.rides.set([]);
        this.loading.set(true);
        this.firstUpcomingRide = null;
        const ridesData: Ride[] = [];
        snapshot.forEach((doc) => {
          ridesData.push({ id: doc.id, ...doc.data() } as Ride);
        });

        const driversToGet: string[] = [];

        for (const ride of ridesData) {
          try {
            // Ensure start and end are Date objects if they are Timestamps
            if (ride.start && typeof ride.start.toDate === 'function') {
              ride.start = ride.start.toDate();
            }
            if (ride.end && typeof ride.end.toDate === 'function') {
              ride.end = ride.end.toDate();
            }
          } catch (e: any) {
            console.error(e);
          }

          const isBetween = moment(ride.start).isBetween(
            this.daypartDay?.start,
            this.daypartDay?.end,
            null,
            '[)', // '[) = equals or between instead of only between
          );
          if (isBetween && !ride.finished) {
            ride.belongsToDaypart = true;
            if (!this.firstUpcomingRide) {
              ride.upcoming = true;
              ride.belongsToDaypart = false; // prevent overwrite in UI
              this.firstUpcomingRide = ride;
            }
          }

          if (
            ride.driverId &&
            driversToGet.indexOf(ride.driverId) === -1 &&
            ride.driverId !== this.driverId
          ) {
            driversToGet.push(ride.driverId);
          }

          this.rides.update((current) => [
            ...current,
            ride?.rideVersion == 2 ? ride : updateRideToV2(ride),
          ]);
        }

        if (driversToGet.length > 0) {
          for (const driverId of driversToGet) {
            const driver = (
              await getDoc(doc(db, `organisations/${this.organisationId}/users/${driverId}`))
            ).data() as User;
            if (!driver) {
              // console.log(`Driver with ID ${driverId} not found.`);
              continue;
            }
            driver.id = driverId;
            this.drivers.update((current) => [...current, driver]);
          }
        }
        // console.log('rides: ', this.rides);
        this.loading.set(false);
      },
      (error) => {
        console.error('Error fetching rides: ', error);
        this.loading.set(false);
      },
    );
  }

  ngOnDestroy(): void {
    this.ridesUnsub?.();
  }

  getDriver(driverId: string): User | undefined {
    return this.drivers().find((driver) => driver.id === driverId);
  }
}
